import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { getAllSensors } from "../../pages/Events/unitEventsUtils";
import Spinner from "./Spinner";
import type { GrantDraft } from "../../services/grantDraft";

type Property = { buildingId: string; name: string };

type Props = {
  value: GrantDraft;
  properties: Property[];
  onChange: (next: GrantDraft) => void;
  disabled?: boolean;
};

type SensorRow = {
  /** The serial. `SensorDto.Name` -- NOT the display name, which is `name` on
   *  the DynamoDB row and `displayName` on the wire. */
  name: string;
  displayName?: string;
  /**
   * `sensorGroups`, because that is what the API sends: `SensorDto.SensorGroups`
   * under `Program.cs`'s camelCase naming policy. The DynamoDB attribute is
   * spelled `sensorsgroups` and this read was written against that spelling,
   * which silently put EVERY sensor under "Not at a property" -- a missing field
   * is undefined, not an error. Pinned by a test against the dev fixture.
   */
  sensorGroups?: string[];
};

/** A building worth offering: one the sensors themselves say they are at. */
type Bucket = { key: string; name: string; sensors: SensorRow[] };

const UNPLACED = "Not at a property";
const UNKNOWN_PROPERTY = "Property no longer listed";

/**
 * The access editor for a User, who reaches exactly one sensor.
 *
 * A different control from `ScopePicker` rather than a configured version of it,
 * because it answers a different question. The scope picker asks "which properties,
 * with which exceptions"; a User has no properties and no exceptions.
 *
 * **Building first, then the sensor at it** -- which is how the people using this
 * think about their portfolio, and keeps the second list short at a tenant with
 * hundreds of sensors.
 *
 * A flat list of every sensor stood here for one revision, because the first
 * building-then-sensor version broke in two ways worth not repeating. Both are
 * designed out rather than traded away:
 *
 * - **A property whose own `sensors` list was empty offered nothing.** The building
 *   list is no longer built from the property rows at all. It is derived from the
 *   sensors' own `sensorGroups`, so a building appears exactly when a sensor says it
 *   is there, and the property row supplies only the display name.
 * - **Two properties sharing a name were indistinguishable.** A name held by more
 *   than one building in the list carries its id, so the choice is never a guess.
 *
 * It is still ONE request, not one per property: the whole roster arrives from
 * `getAllSensors` and the grouping is local.
 *
 * The grant written is a bare `includeSensors: [serial]` with nothing else. The
 * building is navigation and is never part of it -- writing navigation into the
 * grant is what let an admin store a property AND a sensor.
 */
export default function SingleSensorPicker({
  value,
  properties,
  onChange,
  disabled = false,
}: Props) {
  const chosenSerial = value.includeSensors[0] ?? "";

  const sensors = useQuery({
    queryKey: ["all-sensors"],
    // The whole response, as every consumer of a sensors key does; unwrapping in
    // a queryFn is what put two shapes under one key and crashed a picker before.
    queryFn: async () => await getAllSensors(),
    staleTime: 5 * 60_000,
    retry: false,
  });

  const rows: SensorRow[] = Array.isArray(sensors.data)
    ? (sensors.data as SensorRow[])
    : Array.isArray((sensors.data as { data?: unknown })?.data)
      ? (sensors.data as { data: SensorRow[] }).data
      : [];

  const propertyName = new Map(properties.map((p) => [p.buildingId, p.name]));

  /**
   * Which building a sensor is offered under.
   *
   * `sensorGroups` is a list and membership is additive, so a sensor can name more
   * than one property. It takes the first one this tenant can see, not `[0]`
   * blindly: reading `[0]` put a sensor whose first membership is a property this
   * tenant no longer lists -- a stale id, which the live table has -- under "Not at
   * a property", which says the opposite of what is true and is the one thing an
   * admin looking for that sensor would act on. One building per sensor either way:
   * this control picks a sensor, not a placement, and the same serial offered under
   * two buildings would read as two devices.
   */
  const placementOf = (row: SensorRow) => {
    const ids = row.sensorGroups ?? [];
    const placed = ids.find((id) => propertyName.has(id));
    return placed ?? (ids.length > 0 ? UNKNOWN_PROPERTY : UNPLACED);
  };

  // Derived from the sensors, NOT from `properties`: a building is offered exactly
  // when something is at it, so a property row with an empty `sensors` list cannot
  // produce an empty dead end, and one the tenant can no longer name still collects
  // its sensors rather than losing them.
  const byKey = rows.reduce<Map<string, SensorRow[]>>((acc, row) => {
    const key = placementOf(row);
    acc.set(key, [...(acc.get(key) ?? []), row]);
    return acc;
  }, new Map());

  // The two catch-alls sort last, in that order: a sensor at a property this tenant
  // cannot name is a different situation from one at no property at all.
  const rank = (key: string) =>
    key === UNPLACED ? 2 : key === UNKNOWN_PROPERTY ? 1 : 0;

  const named = (key: string) => propertyName.get(key) ?? key;

  // A name held by more than one building carries its id. Without this the two read
  // identically in the dropdown and the right one is unguessable -- which is half of
  // why the earlier building-first version was abandoned.
  const nameCount = Array.from(byKey.keys()).reduce<Map<string, number>>(
    (acc, key) => acc.set(named(key), (acc.get(named(key)) ?? 0) + 1),
    new Map()
  );

  const buckets: Bucket[] = Array.from(byKey.entries())
    .map(([key, items]) => ({
      key,
      name:
        (nameCount.get(named(key)) ?? 0) > 1
          ? `${named(key)} · ${key}`
          : named(key),
      sensors: items,
    }))
    .sort((a, b) =>
      rank(a.key) !== rank(b.key)
        ? rank(a.key) - rank(b.key)
        : a.name.localeCompare(b.name)
    );

  // Null until the admin picks one, so a grant that already names a sensor opens at
  // that sensor's building rather than at an empty second list they have to
  // re-navigate to see what is set. Not an effect: the roster arrives async, and an
  // effect seeding state from it would race the first render and then fight any
  // later refetch.
  const [picked, setPicked] = useState<string | null>(null);
  const chosenRow = rows.find((row) => row.name === chosenSerial);
  const building = picked ?? (chosenRow ? placementOf(chosenRow) : "");

  const atBuilding =
    buckets.find((bucket) => bucket.key === building)?.sensors ?? [];

  const choose = (serial: string) =>
    onChange({
      ...value,
      allProperties: false,
      // No properties and no exclusions. A User's grant is the one serial.
      groupIds: [],
      includeSensors: serial ? [serial] : [],
      excludeSensors: [],
    });

  const selectClass =
    "w-full rounded-control border border-black/15 bg-white px-3 py-2 text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 disabled:bg-black/5 disabled:text-ink-3";

  return (
    <div className="space-y-4">
      <p className="text-ink-2 text-sm">
        A User reaches one sensor. Choose the building, then the sensor.
      </p>

      {sensors.isPending ? (
        <div className="flex items-center gap-2 py-2">
          <Spinner size={16} color="currentColor" />
          <span className="text-ink-2 text-sm">Loading sensors…</span>
        </div>
      ) : sensors.isError ? (
        <p className="text-alert-critical text-sm py-2">
          We couldn't load the sensors. Reload to try again.
        </p>
      ) : rows.length === 0 ? (
        <p className="text-ink-2 text-sm py-2">There are no sensors here yet.</p>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="block text-sm font-medium mb-1">Building</span>
            <select
              className={selectClass}
              value={building}
              disabled={disabled}
              aria-label="Building"
              onChange={(e) => setPicked(e.target.value)}
            >
              <option value="" disabled>
                Choose a building
              </option>
              {buckets.map((bucket) => (
                <option key={bucket.key} value={bucket.key}>
                  {bucket.name}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="block text-sm font-medium mb-1">Sensor</span>
            <select
              className={selectClass}
              value={
                atBuilding.some((s) => s.name === chosenSerial) ? chosenSerial : ""
              }
              disabled={disabled || building === ""}
              aria-label="Sensor"
              onChange={(e) => choose(e.target.value)}
            >
              {/* A placeholder, not a choice: the server refuses a User reaching
                  nothing, so offering it would invite an error. */}
              <option value="" disabled>
                {building === "" ? "Choose a building first" : "Choose a sensor"}
              </option>
              {atBuilding.map((s) => (
                <option key={s.name} value={s.name}>
                  {s.displayName || s.name}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}

      {chosenSerial && !chosenRow && !sensors.isPending && (
        // The grant names a sensor this roster does not contain -- it may have been
        // removed, or moved out of reach. Saying so beats two dropdowns that look
        // like nothing is set.
        <p className="text-ink-2 text-sm">
          Currently set to <span className="font-semibold">{chosenSerial}</span>,
          which is not in the lists above. Choosing a sensor will replace it.
        </p>
      )}
    </div>
  );
}
